import type {ReactElement} from 'react';
import React from 'react';
import {isCustomPosterType} from '@Components/poster-editor/library/poster-editor-library';
import {PosterTemplatePanel} from '@Components/poster-template-panel/poster-template-panel';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {onClickTemplateItem} from '@Libraries/editor-templates-library';
import type {MultipageTemplateData} from '@Libraries/editor-templates-library';
import {MultipageTemplatePagesPanel} from '@Components/multipage-template-pages-panel';
import {TemplatesViewType, setTemplatesView, setSelectedTemplate} from '@Components/poster-template-panel/poster-template-panel.reducer';

interface TemplatesPopupContentProps {
  isOpenedInMobileModal?: boolean;
  onAddTemplateCompleted?(): void;
  isContentVisibleToUser?: boolean;
  showBackButton?: boolean;
  onBack?(): void;
  className?: string;
}

export function TemplatesPopupContent({isOpenedInMobileModal = false, isContentVisibleToUser = true, ...props}: TemplatesPopupContentProps): ReactElement<any> {
  const dispatch = useAppDispatch();
  const currentView = useAppSelector((state) => state.templatesPopup.currentView);
  const selectedTemplate = useAppSelector((state) => state.templatesPopup.selectedTemplate);

  const posterType = useAppSelector((state) => {
    return state.posterEditor.posterObject?.type;
  });
  const customPosterWidth = useAppSelector((state) => {
    return state.posterEditor.posterObject?.userWidth;
  });
  const customPosterHeight = useAppSelector((state) => {
    return state.posterEditor.posterObject?.userHeight;
  });
  const posterWidth = useAppSelector((state) => {
    return state.posterEditor.posterObject?.width;
  });
  const posterHeight = useAppSelector((state) => {
    return state.posterEditor.posterObject?.height;
  });

  if (!posterWidth || !posterHeight) {
    throw new Error('whiteboard does not exist in templates popup');
  }

  const getPosterHeight = (): number => {
    if (isCustomPosterType() && customPosterHeight) {
      return customPosterHeight;
    }
    return posterHeight;
  };

  const getPosterWidth = (): number => {
    if (isCustomPosterType() && customPosterWidth) {
      return customPosterWidth;
    }
    return posterWidth;
  };

  const onAddTemplate = async (id: string): Promise<void> => {
    await onClickTemplateItem(id);
    if (props.onAddTemplateCompleted) {
      props.onAddTemplateCompleted();
    }
  };

  const handleAddSinglePage = async (templateHashedId: string, pageHashedId: string): Promise<void> => {
    await onClickTemplateItem(templateHashedId, pageHashedId);
    if (props.onAddTemplateCompleted) {
      props.onAddTemplateCompleted();
    }
  };

  const handleMultipageTemplateClick = (template: MultipageTemplateData): void => {
    dispatch(setSelectedTemplate(template));
    dispatch(setTemplatesView(TemplatesViewType.MULTIPAGE_DETAILS));
  };

  const handleBackToTemplatesGrid = (): void => {
    dispatch(setTemplatesView(TemplatesViewType.TEMPLATES_GRID));
    dispatch(setSelectedTemplate(null));
  };

  return (
    <div className={`${props.className} ${isOpenedInMobileModal ? '' : '_full-height'}`}>
      <div className={`_full-height ${currentView === TemplatesViewType.TEMPLATES_GRID ? '' : '_hidden'}`}>
        <PosterTemplatePanel
          showBackButton={props.showBackButton}
      onBack={(): void => {
        if (props.onBack) {
          props.onBack();
        }
      }}
      onAddTemplate={onAddTemplate}
      onMultipageTemplateClick={handleMultipageTemplateClick}
          posterTypeName={posterType?.name}
          sendHeightWidthParamsForPosterType={isCustomPosterType()}
          posterHeight={getPosterHeight()}
          posterWidth={getPosterWidth()}
          isOpenedInMobileModal={isOpenedInMobileModal}
          idGalleryTemplate={window.posterEditor?.whiteboard?.getGalleryTemplateId()}
          isContentVisibleToUser={isContentVisibleToUser && currentView === TemplatesViewType.TEMPLATES_GRID}
        />
      </div>

      {selectedTemplate ? (
        <div className={`_full-height ${currentView === TemplatesViewType.MULTIPAGE_DETAILS ? '' : '_hidden'}`}>
          <MultipageTemplatePagesPanel
            template={selectedTemplate}
            onBack={handleBackToTemplatesGrid}
            onAddAllPages={onAddTemplate}
            onAddSinglePage={handleAddSinglePage}
            posterWidth={getPosterWidth()}
            posterHeight={getPosterHeight()}
            isOpenedInMobileModal={isOpenedInMobileModal}
          />
        </div>
      ) : null}
    </div>
  );
}
